import React from 'react';
import { Row, Toggle } from '../settingsPrimitives';

/**
 * What a connected MCP client may do, one row per surface.
 *
 * **The capability keys here are the contract with Rust.** Every
 * `ToolDef.surface` in `src-tauri/src/mcp/registry.rs` must appear as a key in
 * `SURFACES`, and every key here must have tools behind it — a toggle for a
 * surface with no tools is a promise the server cannot keep, and a tool whose
 * surface has no toggle is a capability the user can never grant. A Rust test
 * scans this file for each key and a jest test scans `registry.rs` for each
 * row, so neither direction can drift alone.
 *
 * `availableSurfaces` comes from the server (derived from `TOOLS`), so a row
 * whose tools are not in this build simply does not render.
 *
 * The `boards` row gained its write toggle in phase 2, when eight board write
 * tools landed behind `Surface::Boards` + `Access::Write`. Until then it was
 * `write: false` on purpose — a switch that grants nothing is worse than no
 * switch — and `mcp.capBoardsReadDesc` said so in the user's own words. Both
 * halves had to move together, or the page would have been lying in one
 * direction or the other.
 *
 * **O5, as amended by the owner: no warning banner.** The honest limit of the
 * credential scrub is one plain sentence of ordinary body copy at the bottom
 * of this section, at the same visual weight as every other description on the
 * page. No callout, no `⚠`, no coloured strip. The full explanation lives in
 * the tool descriptions and in `server/discover`'s `instructions`, which is
 * where an agent reads it anyway.
 */
export const SURFACES = [
  {
    key: 'documents',
    labelKey: 'mcp.capDocuments',
    readDescKey: 'mcp.capDocumentsReadDesc',
    writeDescKey: 'mcp.capDocumentsWriteDesc',
    write: true,
  },
  {
    key: 'memory',
    labelKey: 'mcp.capMemory',
    readDescKey: 'mcp.capMemoryReadDesc',
    writeDescKey: 'mcp.capMemoryWriteDesc',
    write: true,
  },
  {
    key: 'boards',
    labelKey: 'mcp.capBoards',
    readDescKey: 'mcp.capBoardsReadDesc',
    writeDescKey: 'mcp.capBoardsWriteDesc',
    write: true,
  },
  {
    key: 'calendar',
    labelKey: 'mcp.capCalendar',
    readDescKey: 'mcp.capCalendarReadDesc',
    writeDescKey: 'mcp.capCalendarWriteDesc',
    write: true,
  },
  // Phase 5. The read half is deliberately NARROW and the copy says so: a
  // client sees names and methods, never a URL, a header or a body. The write
  // half is create-only in this build and is STRICTER than the editor beside
  // it — a literal credential in an auth block is refused — which is worth a
  // sentence, because a user who watches an agent get refused should know it
  // was on purpose.
  {
    key: 'collections',
    labelKey: 'mcp.capCollections',
    readDescKey: 'mcp.capCollectionsReadDesc',
    writeDescKey: 'mcp.capCollectionsWriteDesc',
    write: true,
  },
  // Phase 6. **The only surface here that reaches another machine**, and the
  // copy has to say so plainly rather than reading like another data family.
  // Two things the user cannot infer from the label and both are load-bearing:
  // this grant is inert on its own (a connection has to be ticked below, and
  // the destructive switch has to be on), and every single command is shown to
  // them before it runs. The honest limit of what happens to the OUTPUT is the
  // sentence under the allowlist, in `McpTerminalGrants`, next to the servers
  // it applies to.
  {
    key: 'terminal',
    labelKey: 'mcp.capTerminal',
    readDescKey: 'mcp.capTerminalReadDesc',
    writeDescKey: 'mcp.capTerminalWriteDesc',
    write: true,
  },
  // Graph & Projects. READ ONLY — no write row, because no graph tool writes
  // and a switch that grants nothing is worse than no switch. Its own grant
  // because the graph carries names from several surfaces at once, and the
  // copy says exactly that, plus what it never includes.
  {
    key: 'graph',
    labelKey: 'mcp.capGraph',
    readDescKey: 'mcp.capGraphReadDesc',
    write: false,
  },
  // Attachments (card 7de79076). WRITE ONLY: media_upload reads a file from a
  // folder the user picked below the matrix and uploads it, encrypted.
  {
    key: 'attachments',
    labelKey: 'mcp.capAttachments',
    writeDescKey: 'mcp.capAttachmentsWriteDesc',
    read: false,
    write: true,
  },
];

export default function McpCapabilityMatrix({ t, capabilities, availableSurfaces, onChange, disabled }) {
  const available = Array.isArray(availableSurfaces) ? availableSurfaces : [];
  const rows = SURFACES.filter((s) => available.includes(s.key));

  const set = (key, access, value) => {
    const next = { ...(capabilities || {}) };
    next[key] = { ...(next[key] || {}), [access]: value };
    onChange(next);
  };

  // "Allow all" covers exactly the toggles this matrix renders: only surfaces
  // in this build, and write only where the row has one. The destructive
  // switch is its own section and stays out of it on purpose.
  const allOn =
    rows.length > 0 &&
    rows.every(
      (s) =>
        (s.read === false || capabilities?.[s.key]?.read) && (!s.write || capabilities?.[s.key]?.write)
    );
  const setAll = (value) => {
    const next = { ...(capabilities || {}) };
    rows.forEach((s) => {
      next[s.key] = {
        ...(next[s.key] || {}),
        ...(s.read === false ? {} : { read: value }),
        ...(s.write ? { write: value } : {}),
      };
    });
    onChange(next);
  };

  return (
    <>
      {rows.length > 0 && (
        <Row label={t('mcp.capAllowAll')} desc={t('mcp.capAllowAllDesc')}>
          <Toggle value={allOn} disabled={disabled} onChange={setAll} />
        </Row>
      )}
      {rows.map((surface) => (
        <React.Fragment key={surface.key}>
          {/* `read: false` marks a write-only surface (Attachments). */}
          {surface.read !== false && (
            <Row
              label={`${t(surface.labelKey)} — ${t('mcp.capRead')}`}
              desc={t(surface.readDescKey)}
            >
              <Toggle
                value={!!capabilities?.[surface.key]?.read}
                disabled={disabled}
                onChange={(v) => set(surface.key, 'read', v)}
              />
            </Row>
          )}
          {surface.write && (
            <Row
              label={`${t(surface.labelKey)} — ${t('mcp.capWrite')}`}
              desc={t(surface.writeDescKey)}
            >
              <Toggle
                value={!!capabilities?.[surface.key]?.write}
                disabled={disabled}
                onChange={(v) => set(surface.key, 'write', v)}
              />
            </Row>
          )}
        </React.Fragment>
      ))}
      <div className="settings-row-desc mcp-note">{t('mcp.freeTextNote')}</div>
      {/* R-7: `scopes_list` is Surface::Any — the OR of the grantable reads — so a
          memory-only grant still reaches the team roster. The gate is deliberate;
          what was missing is that the page said nothing about it. */}
      <div className="settings-row-desc mcp-note">{t('mcp.scopesNote')}</div>
    </>
  );
}
